import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import * as React from 'react';

import { cn } from '../../lib/utils';

export const DropdownRoot      = RadixDropdown.Root;
export const DropdownTrigger   = RadixDropdown.Trigger;
export const DropdownPortal    = RadixDropdown.Portal;
export const DropdownGroup     = RadixDropdown.Group;
export const DropdownSub       = RadixDropdown.Sub;
export const DropdownSubTrigger = RadixDropdown.SubTrigger;

export const DropdownContent = React.forwardRef<
  React.ElementRef<typeof RadixDropdown.Content>,
  React.ComponentPropsWithoutRef<typeof RadixDropdown.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <RadixDropdown.Portal>
    <RadixDropdown.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 min-w-48 overflow-hidden rounded-xl border border-border-subtle bg-surface-overlay p-1 shadow-lg',
        'animate-in fade-in slide-in-from-top-2 duration-base',
        className,
      )}
      {...props}
    />
  </RadixDropdown.Portal>
));
DropdownContent.displayName = RadixDropdown.Content.displayName;

export const DropdownItem = React.forwardRef<
  React.ElementRef<typeof RadixDropdown.Item>,
  React.ComponentPropsWithoutRef<typeof RadixDropdown.Item>
>(({ className, ...props }, ref) => (
  <RadixDropdown.Item
    ref={ref}
    className={cn(
      'flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 text-sm text-text-primary outline-none',
      'hover:bg-bg-subtle focus:bg-bg-subtle',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
      className,
    )}
    {...props}
  />
));
DropdownItem.displayName = RadixDropdown.Item.displayName;

export const DropdownDestructiveItem = React.forwardRef<
  React.ElementRef<typeof RadixDropdown.Item>,
  React.ComponentPropsWithoutRef<typeof RadixDropdown.Item>
>(({ className, ...props }, ref) => (
  <RadixDropdown.Item
    ref={ref}
    className={cn(
      'flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 text-sm text-danger-default outline-none',
      'hover:bg-danger-subtle focus:bg-danger-subtle',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
      className,
    )}
    {...props}
  />
));
DropdownDestructiveItem.displayName = 'DropdownDestructiveItem';

export const DropdownSeparator = React.forwardRef<
  React.ElementRef<typeof RadixDropdown.Separator>,
  React.ComponentPropsWithoutRef<typeof RadixDropdown.Separator>
>(({ className, ...props }, ref) => (
  <RadixDropdown.Separator
    ref={ref}
    className={cn('mx-1 my-1 h-px bg-border-subtle', className)}
    {...props}
  />
));
DropdownSeparator.displayName = RadixDropdown.Separator.displayName;

export const DropdownLabel = React.forwardRef<
  React.ElementRef<typeof RadixDropdown.Label>,
  React.ComponentPropsWithoutRef<typeof RadixDropdown.Label>
>(({ className, ...props }, ref) => (
  <RadixDropdown.Label
    ref={ref}
    className={cn(
      'px-3 py-1 text-xs font-medium uppercase tracking-wider text-text-tertiary',
      className,
    )}
    {...props}
  />
));
DropdownLabel.displayName = RadixDropdown.Label.displayName;

export const DropdownCheckboxItem = React.forwardRef<
  React.ElementRef<typeof RadixDropdown.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof RadixDropdown.CheckboxItem>
>(({ className, ...props }, ref) => (
  <RadixDropdown.CheckboxItem
    ref={ref}
    className={cn(
      'flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 text-sm text-text-primary outline-none',
      'hover:bg-bg-subtle focus:bg-bg-subtle',
      className,
    )}
    {...props}
  />
));
DropdownCheckboxItem.displayName = RadixDropdown.CheckboxItem.displayName;
